import { twMerge } from '@shared/themes';
import { ROUTES } from '@shared/utils';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import type { ComponentProps } from 'react';
import type { LotteryName } from '../../../lottery-purchases/types';

type ViewArchiveProps = ComponentProps<'div'> & {
  lotteryName: LotteryName;
  align?: 'start' | 'end';
};

/**
 * ViewArchive component displays a label and a link to view the archive of lottery results.
 *
 * It uses translations for the label and link text, and constructs the archive URL
 * based on the DRAW_HISTORY route for the given lottery.
 * Expected to be used from {@link LotteryLayout} on the latest-results header.
 *
 * @param {LotteryName} lotteryName - The name of the lottery.
 * @param {ViewArchiveProps} props - The props for the ViewArchive component.
 * @param {'start' | 'end'} [props.align='end'] - `end` stacks and right-aligns on xl. `start` keeps the label and link on one line.
 * @param {string} [props.className] - Optional additional class names for styling.
 * @returns {JSX.Element} The rendered ViewArchive component.
 */

function ViewArchive({ lotteryName, className, align = 'end', ...props }: ViewArchiveProps) {
  const t = useTranslations('lotteries.results');

  const { DRAW_HISTORY } = ROUTES.LOTTO;

  const classes = {
    container: twMerge(align === 'end' ? 'mx-auto flex gap-x-1 xl:flex-col xl:items-end' : 'flex gap-x-1', className),
    label: twMerge('paragraph-explanatory', align === 'end' && 'xl:paragraph'),
    link: twMerge('link-label-explanatory underline', align === 'end' && 'xl:link-label'),
  };

  return (
    <div className={classes.container} {...props}>
      <p className={classes.label}>{t('more_results_label')}</p>
      <Link href={DRAW_HISTORY[lotteryName.toUpperCase() as keyof typeof DRAW_HISTORY] ?? ''} className={classes.link} data-track-name="View Archive">
        {t('view_archive')}
      </Link>
    </div>
  );
}

export { ViewArchive };
